import * as THREE from 'three';
import { TrackRuntime } from '../core/track/runtime';
import { TrackDef } from '../core/track/spline';
import { curvatureComb } from '../core/track/smooth';
import { Section } from '../core/track/sections';

const NODE_COLOR = 0x2f7fe0;
const NODE_SELECTED = 0xffa726;

const SCRUB_HALF_LEN = 3.5; // m of track highlighted around the hover s
const SCRUB_COLOR = 0xffd54f;

/** Visual node marker radius (metres). */
const NODE_RADIUS = 0.55;
/** Invisible pick-shell radius (metres) so markers stay hittable when small on screen. */
const NODE_PICK_RADIUS = 1.15;
/** Extra screen-pixel hit radius when the sphere projects smaller than this. */
const PICK_PX = 14;

export type OverlayPick = { kind: 'node'; nodeId: string };

/** Node spheres, curvature comb and section highlights. */
export class Overlays {
  readonly group = new THREE.Group();
  /** Scrub marker lives outside `group` so it stays visible in simulate mode. */
  readonly scrubGroup = new THREE.Group();
  private nodeMeshes = new Map<string, THREE.Mesh>();
  private combLines: THREE.LineSegments | null = null;
  private sectionLines: THREE.Line[] = [];
  private scrubLine: THREE.Line | null = null;
  private scrubRing: THREE.Line | null = null;
  private nodeGeo = new THREE.SphereGeometry(NODE_RADIUS, 14, 10);
  /** Larger invisible shell so nodes stay easy to click when zoomed out. */
  private nodePickGeo = new THREE.SphereGeometry(NODE_PICK_RADIUS, 10, 8);
  private nodeMat = new THREE.MeshBasicMaterial({ color: NODE_COLOR, depthTest: false, transparent: true, opacity: 0.9 });
  private selMat = new THREE.MeshBasicMaterial({ color: NODE_SELECTED, depthTest: false, transparent: true, opacity: 0.95 });
  private pickMat = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
  });
  private scrubMat = new THREE.LineBasicMaterial({
    color: SCRUB_COLOR,
    depthTest: false,
    transparent: true,
    opacity: 0.95,
  });
  private pickNdc = new THREE.Vector3();
  private pickWorld = new THREE.Vector3();
  private pickClosest = new THREE.Vector3();
  private pickCamPos = new THREE.Vector3();
  private pickRaycaster = new THREE.Raycaster();
  private pickMouse = new THREE.Vector2();

  showComb = false;

  constructor() {
    this.group.name = 'overlays';
    this.group.renderOrder = 10;
    this.scrubGroup.name = 'scrub';
    this.scrubGroup.renderOrder = 13;
  }

  /** Rebuild node spheres to match the document. */
  syncNodes(track: TrackDef, selection: Set<string>): void {
    const seen = new Set<string>();
    for (const node of track.nodes) {
      seen.add(node.id);
      let mesh = this.nodeMeshes.get(node.id);
      if (!mesh) {
        mesh = new THREE.Mesh(this.nodeGeo, this.nodeMat);
        mesh.userData = { kind: 'node', nodeId: node.id };
        mesh.renderOrder = 11;
        // Invisible larger child — Raycaster skips visible:false, so opacity 0 instead.
        const pick = new THREE.Mesh(this.nodePickGeo, this.pickMat);
        pick.userData = { kind: 'node', nodeId: node.id };
        pick.renderOrder = 11;
        mesh.add(pick);
        this.nodeMeshes.set(node.id, mesh);
        this.group.add(mesh);
      }
      mesh.position.copy(node.p);
      mesh.material = selection.has(node.id) ? this.selMat : this.nodeMat;
      for (const child of mesh.children) {
        child.userData = { kind: 'node', nodeId: node.id };
      }
    }
    for (const [id, mesh] of this.nodeMeshes) {
      if (!seen.has(id)) {
        this.group.remove(mesh);
        this.nodeMeshes.delete(id);
      }
    }
  }

  /** Pickable meshes for the editor raycaster (visual + invisible hit shells). */
  pickables(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const mesh of this.nodeMeshes.values()) {
      out.push(mesh, ...mesh.children);
    }
    return out;
  }

  /**
   * Pick a node under the cursor. Hits the full visual/pick sphere (so the
   * marker is not click-through) and expands to `PICK_PX` when zoomed out.
   * Closest sphere along the camera ray wins when several overlap.
   */
  pickAtScreen(
    camera: THREE.Camera,
    clientX: number,
    clientY: number,
    canvas: HTMLCanvasElement,
    maxPx = PICK_PX,
  ): OverlayPick | null {
    if (!this.group.visible) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return null;

    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    this.pickMouse.x = (mx / rect.width) * 2 - 1;
    this.pickMouse.y = -(my / rect.height) * 2 + 1;

    camera.updateMatrixWorld(true);
    if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
      (camera as THREE.PerspectiveCamera).updateProjectionMatrix();
    }
    this.pickRaycaster.setFromCamera(this.pickMouse, camera);
    camera.getWorldPosition(this.pickCamPos);

    const ray = this.pickRaycaster.ray;
    const vFov =
      (camera as THREE.PerspectiveCamera).isPerspectiveCamera
        ? ((camera as THREE.PerspectiveCamera).fov * Math.PI) / 180
        : Math.PI / 3;
    const pxToWorldAt = (depth: number) => (2 * Math.tan(vFov * 0.5) * depth) / rect.height;

    let best: OverlayPick | null = null;
    let bestScore = Infinity;

    const consider = (world: THREE.Vector3, nodeId: string) => {
      this.pickClosest.copy(world).sub(this.pickCamPos);
      const depth = this.pickClosest.dot(ray.direction);
      if (depth <= 0.05) return;

      ray.closestPointToPoint(world, this.pickClosest);
      const perp = this.pickClosest.distanceTo(world);
      const worldTol = Math.max(NODE_PICK_RADIUS, pxToWorldAt(depth) * maxPx);
      if (perp > worldTol) return;

      this.pickNdc.copy(world).project(camera);
      if (this.pickNdc.z < -1 || this.pickNdc.z > 1) return;

      // Prefer the nearer sphere so a marker in front of a stub/foot/track
      // consumes the click instead of whatever sits behind it.
      const score = depth + perp * 0.01;
      if (score < bestScore) {
        bestScore = score;
        best = { kind: 'node', nodeId };
      }
    };

    for (const [id, mesh] of this.nodeMeshes) {
      consider(mesh.getWorldPosition(this.pickWorld), id);
    }
    return best;
  }

  /** Visual root mesh for a node (for translate-gizmo attach). */
  meshFor(kind: OverlayPick['kind'], nodeId: string): THREE.Object3D | undefined {
    if (kind === 'node') return this.nodeMeshes.get(nodeId);
    return undefined;
  }

  updateComb(runtime: TrackRuntime): void {
    if (this.combLines) {
      this.group.remove(this.combLines);
      this.combLines.geometry.dispose();
      this.combLines = null;
    }
    if (!this.showComb) return;
    const quills = curvatureComb(runtime.centerline, 1.2);
    const pts: THREE.Vector3[] = [];
    const scale = 25;
    let prevTip: THREE.Vector3 | null = null;
    for (const q of quills) {
      const tip = q.pos.clone().addScaledVector(q.dir, Math.min(q.curvature * scale, 12));
      pts.push(q.pos, tip);
      if (prevTip) pts.push(prevTip, tip); // connect quill tips into a comb spine
      prevTip = tip;
    }
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    this.combLines = new THREE.LineSegments(
      geo,
      new THREE.LineBasicMaterial({ color: 0xe754c0, transparent: true, opacity: 0.8 }),
    );
    this.group.add(this.combLines);
  }

  /** Coloured lines floating above the track marking station / brake ranges. */
  updateSections(runtime: TrackRuntime, sections: Section[]): void {
    for (const l of this.sectionLines) {
      this.group.remove(l);
      l.geometry.dispose();
      (l.material as THREE.Material).dispose();
    }
    this.sectionLines = [];
    const colors: Record<string, number> = { station: 0x4dd0e1, brake: 0xef5350 };
    for (const sec of sections) {
      const pts: THREE.Vector3[] = [];
      const L = runtime.totalLength;
      let from = sec.from;
      let to = sec.to;
      if (to < from) to += L;
      for (let s = from; s <= to; s += 1) {
        const f = runtime.frameAt(runtime.centerline.normalizeS(s));
        pts.push(f.pos.clone().addScaledVector(f.up, 0.9));
      }
      if (pts.length < 2) continue;
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: colors[sec.kind], linewidth: 2, transparent: true, opacity: 0.9 }),
      );
      line.renderOrder = 9;
      this.sectionLines.push(line);
      this.group.add(line);
    }
  }

  /** Highlight a short band of track at arc-length s (null clears). */
  setScrub(runtime: TrackRuntime | null, s: number | null): void {
    this.clearScrub();
    if (!runtime || s === null || runtime.totalLength < 1e-6) return;

    const L = runtime.totalLength;
    const s0 = runtime.centerline.normalizeS(s);
    const half = Math.min(SCRUB_HALF_LEN, L * 0.5);
    const pts: THREE.Vector3[] = [];
    for (let d = -half; d <= half; d += 0.35) {
      const f = runtime.frameAt(runtime.centerline.normalizeS(s0 + d));
      pts.push(f.pos.clone().addScaledVector(f.up, 0.55));
    }
    if (pts.length >= 2) {
      this.scrubLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), this.scrubMat);
      this.scrubLine.renderOrder = 14;
      this.scrubGroup.add(this.scrubLine);
    }

    // Cross-section ring so the exact hover point is obvious from any angle.
    const f = runtime.frameAt(s0);
    const r = 1.15;
    const ring: THREE.Vector3[] = [];
    for (let i = 0; i <= 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      ring.push(
        f.pos
          .clone()
          .addScaledVector(f.right, Math.cos(a) * r)
          .addScaledVector(f.up, Math.sin(a) * r),
      );
    }
    this.scrubRing = new THREE.Line(new THREE.BufferGeometry().setFromPoints(ring), this.scrubMat);
    this.scrubRing.renderOrder = 15;
    this.scrubGroup.add(this.scrubRing);
  }

  private clearScrub(): void {
    if (this.scrubLine) {
      this.scrubGroup.remove(this.scrubLine);
      this.scrubLine.geometry.dispose();
      this.scrubLine = null;
    }
    if (this.scrubRing) {
      this.scrubGroup.remove(this.scrubRing);
      this.scrubRing.geometry.dispose();
      this.scrubRing = null;
    }
  }
}
